import { useMemo } from "react";
import FirmFlowTable from "../../../../components/mytasks/FirmFlowTable";
import ServiceDueDateCell from "../../../../components/reports/ServiceDueDateCell";
import { formatTimestampDate } from "../../../../helpers/formatDateTime";
import {
    getReportStatusBadgeClass,
    getReportStatusLabel,
} from "../../../../helpers/reportStatusOptions";
import { formatInactiveDuration } from "../inactiveReportUtils";

const getServiceSubscriptionId = (row) =>
    row?.clientServiceSubscriptionId ??
    row?.clientServiceId ??
    row?.subscriptionId ??
    row?.id;

const getServiceHref = (row) => {
    const serviceSubscriptionId = getServiceSubscriptionId(row);
    const clientId = row?.clientId;

    return serviceSubscriptionId && clientId
        ? `/clients/service/${serviceSubscriptionId}/client/${clientId}`
        : null;
};

const LinkedValue = ({ row, children }) => {
    const href = getServiceHref(row);

    return href ? (
        <a
            href={href}
            rel="noreferrer"
            className="rounded-full text-gray hover:text-blue-500"
        >
            {children}
        </a>
    ) : (
        <span>{children}</span>
    );
};

const getDisplayNames = (value) => {
    const values = Array.isArray(value) ? value : value ? [value] : [];

    return values
        .map((item) => {
            if (typeof item === "string") return item.trim();

            const directName = item?.name ?? item?.fullName;
            if (typeof directName === "string" && directName.trim()) {
                return directName.trim();
            }

            return `${item?.firstName ?? ""} ${item?.lastName ?? ""}`.trim();
        })
        .filter(Boolean);
};

const NamesCell = ({ value }) => {
    const names = getDisplayNames(value);

    return names.length > 0 ? (
        <div className="space-y-1">
            {names.map((name, index) => (
                <div key={`${name}-${index}`}>{name}</div>
            ))}
        </div>
    ) : (
        <span className="text-slate-500">—</span>
    );
};

const InactiveReportTable = ({ data, resetPageSignal }) => {
    const columns = useMemo(
        () => [
            {
                Header: "Client",
                accessor: "clientName",
                Cell: (props) => (
                    <LinkedValue row={props.row.original}>
                        {props.value || "—"}
                    </LinkedValue>
                ),
            },
            {
                Header: "Service",
                accessor: "serviceName",
                Cell: (props) => (
                    <LinkedValue row={props.row.original}>
                        {props.value || "—"}
                    </LinkedValue>
                ),
            },
            {
                Header: "Team Leader",
                id: "teamLeader",
                accessor: (row) =>
                    row?.teamLeaders ??
                    row?.teamLeaderNames ??
                    row?.teamLeaderName ??
                    row?.teamLeader,
                Cell: (props) => <NamesCell value={props.value} />,
            },
            {
                Header: "Team",
                id: "team",
                accessor: (row) =>
                    row?.teams ?? row?.teamNames ?? row?.teamName ?? row?.team,
                Cell: (props) => <NamesCell value={props.value} />,
            },
            {
                Header: "Due Date",
                id: "dueDate",
                accessor: (row) =>
                    row?.dueDate ??
                    row?.subscriptionEndDate ??
                    row?.clientServiceSubscriptionEndDate,
                Cell: (props) => (
                    <ServiceDueDateCell
                        dueDate={props.value}
                        status={props.row.original.status}
                        dueDateColorClass={
                            props.row.original.dueDateColorClass
                        }
                        dueDateSubLabel={props.row.original.dueDateSubLabel}
                        completionLabel={props.row.original.completionLabel}
                        completionDate={
                            props.row.original.completionDate ??
                            props.row.original.completedDate
                        }
                        href={getServiceHref(props.row.original)}
                    />
                ),
            },
            {
                Header: "Status",
                accessor: "status",
                Cell: (props) => (
                    <span
                        className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-sm font-medium text-white ${getReportStatusBadgeClass(
                            props.value,
                        )}`}
                    >
                        {getReportStatusLabel(props.value)}
                    </span>
                ),
            },
            {
                Header: "Last Activity",
                accessor: "lastActivityAt",
                Cell: (props) => {
                    const exactDate = formatTimestampDate(props.value, {
                        dateStyle: "medium",
                    });
                    const relativeDate = formatInactiveDuration(props.value);

                    return exactDate ? (
                        <div className="inline-flex flex-col whitespace-nowrap">
                            <span>{exactDate}</span>
                            {relativeDate && (
                                <span className="mt-1 text-xs font-medium text-slate-500">
                                    {relativeDate}
                                </span>
                            )}
                        </div>
                    ) : (
                        <span className="text-slate-500">No activity recorded</span>
                    );
                },
            },
        ],
        [],
    );

    return (
        <FirmFlowTable
            columns={columns}
            data={Array.isArray(data) ? data : []}
            resetPageSignal={resetPageSignal}
        />
    );
};

export default InactiveReportTable;
